import type { ReportVersion, VendorReport } from '@/lib/api/vendor-intelligence-report';

/**
 * The Report step's two views (E24-design §9, amended 4 Nov 2026): the
 * published report, which is what the step opens on, and the draft being
 * edited, reached with "Edit draft" (`?view=draft` on the step's URL).
 */
export type ReportStepView = 'published' | 'draft';

/** Why Prepare waits: on the ratings (the server's reason) or on the shortlist (the tab's own gate). */
export interface PrepareBlocker {
  kind: 'ratings' | 'shortlist';
  message: string;
}

/** Prepare report, owned by the step and offered by both views. */
export interface PrepareState {
  run: () => void;
  pending: boolean;
  error: unknown;
  /** The optional focus line sent with the next Prepare. */
  focus: string;
  setFocus: (value: string) => void;
  blocker: PrepareBlocker | null;
}

export const SHORTLIST_BLOCKER =
  'Confirm the shortlist first. The report gives each shortlisted vendor a page and lists the alternates and set-aside vendors with your reasons.';

/**
 * Why Prepare is not available: the server's reason first (prefixed when
 * the ratings are not confirmed yet), then the tab's own shortlist gate
 * (T-0274; ADR-026 decision 3). Null when it is.
 */
export function prepareBlocker(
  report: VendorReport,
  ratingsReady: boolean,
  shortlistReady: boolean,
): PrepareBlocker | null {
  if (report.draft_blocked) {
    return {
      kind: 'ratings',
      message: ratingsReady
        ? report.draft_blocked.message
        : `Confirm ratings first. ${report.draft_blocked.message}`,
    };
  }
  if (!shortlistReady) return { kind: 'shortlist', message: SHORTLIST_BLOCKER };
  return null;
}

/** The published versions, newest first. */
export function versionsNewestFirst(report: VendorReport): ReportVersion[] {
  return [...report.versions].sort((a, b) => b.version - a.version);
}

/** "4 Nov 2026 by Sarah Chen", or the date alone. */
export function byLine(date: string, name: string | null | undefined): string {
  return name ? `${date} by ${name}` : date;
}
